import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { config } from '../config';
import * as fmt from '../format';
import { ROUTE_STROKE, loadMaps, positionAtMiles } from '../maps';
import type { SharedStatus } from '../models';

/// A walk's progress, shown to someone with no account.
///
/// Rendered before the sign-in gate, like the feature page: the whole point is
/// that the person opening it does not have one. The token in the URL is the
/// entire credential, so this page asks for nothing and stores nothing.
///
/// What it shows is decided by the server, not filtered here. A client-side
/// filter would mean the private figures had already been sent.

/// Street View panorama loads are billed, and a shared page can be left open.
/// Only move the camera when the view would actually differ.
const STREET_VIEW_MOVE_METRES = 25;

export function Shared({ token }: { token: string }) {
  const mapHolder = useRef<HTMLDivElement | null>(null);
  const panoHolder = useRef<HTMLDivElement | null>(null);
  const started = useRef(false);
  const lastPano = useRef<google.maps.LatLng | null>(null);

  const [status, setStatus] = useState<SharedStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [coverage, setCoverage] = useState<'unknown' | 'yes' | 'no'>('unknown');

  useEffect(() => {
    void api.sharedStatus(token)
      .then(setStatus)
      .catch((failure: unknown) => {
        setError(failure instanceof Error ? failure.message : String(failure));
      });
  }, [token]);

  // Drawn once the figures and the map are both in. Nothing here polls: a
  // shared link is a look at where someone has got to, not a live feed, and
  // reloading is the obvious way to ask again.
  useEffect(() => {
    if (started.current || !status?.route || !config.mapsKey) return;
    started.current = true;
    const route = status.route;

    void (async () => {
      try {
        const maps = await loadMaps();
        if (!mapHolder.current) return;

        const path = maps.geometry.encoding.decodePath(route.polyline);
        const map = new maps.Map(mapHolder.current, {
          mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
        });
        new maps.Polyline({ path, map, strokeColor: ROUTE_STROKE, strokeWeight: 4 });

        const bounds = new maps.LatLngBounds();
        for (const point of path) bounds.extend(point);
        map.fitBounds(bounds);

        const at = positionAtMiles(maps, path, status.totalActualMiles);
        if (!at) return;
        new maps.Marker({ position: at.point, map, title: status.walkName });

        if (!panoHolder.current) return;
        const panorama = new maps.StreetViewPanorama(panoHolder.current, {
          visible: true, addressControl: false, fullscreenControl: false,
          linksControl: false, clickToGo: false,
        });
        const previous = lastPano.current;
        if (previous && maps.geometry.spherical.computeDistanceBetween(previous, at.point)
            < STREET_VIEW_MOVE_METRES) return;
        lastPano.current = at.point;

        new maps.StreetViewService().getPanorama(
          { location: at.point, radius: 250 },
          (data, state) => {
            const pano = data?.location?.pano;
            if (state === maps.StreetViewStatus.OK && pano) {
              panorama.setPano(pano);
              panorama.setPov({ heading: at.heading, pitch: 0 });
              setCoverage('yes');
            } else {
              setCoverage('no');
            }
          },
        );
      } catch {
        // A map that will not load must not take the figures down with it.
        setCoverage('no');
      }
    })();
  }, [status]);

  if (error) {
    return (
      <main className="shell shell--centred">
        <h1>Steps</h1>
        <p className="error">{error}</p>
        <p className="muted">
          Shared links can be given a lifetime, and this one may simply have reached the end
          of it. Ask for a new one.
        </p>
      </main>
    );
  }

  if (!status) {
    return <main className="shell"><p className="muted">Loading…</p></main>;
  }

  const ahead = status.currentDiffMiles >= 0;

  return (
    <main className="shell shared">
      <header className="shared__head">
        <h1>{status.walkName}</h1>
        <p className="muted">
          {fmt.percent(status.pctComplete)} of the way · day {status.daysElapsed}
          {' · as of '}{fmt.mediumDate(status.asOf)}
        </p>
      </header>

      <div className="grid">
        <section className="card">
          <p className="headline__caption">Walked so far</p>
          <p className="headline__value">{fmt.miles(status.totalActualMiles)}</p>
          <p className="muted">of {fmt.miles(status.distanceMiles, 0)}</p>
        </section>

        <section className="card">
          <p className="headline__caption">Still to go</p>
          <p className="headline__value">{fmt.miles(status.remainingMiles)}</p>
          <p className="muted">{fmt.miles(status.targetPerDayMiles)} a day is the plan</p>
        </section>

        <section className="card">
          <p className="headline__caption">{ahead ? 'Ahead of plan' : 'Behind plan'}</p>
          <p className={`headline__value ${ahead ? 'good' : 'warn'}`}>
            {fmt.signedMiles(status.currentDiffMiles)}
          </p>
          <p className="muted">finishing {fmt.mediumDate(status.estimatedFinishDate)}</p>
        </section>

        <section className="card">
          <p className="headline__caption">Today</p>
          <p className="headline__value">{fmt.miles(status.todayMiles)}</p>
          <p className="muted">so far</p>
        </section>
      </div>

      {status.route ? (
        <>
          <section className="card card--wide">
            <div className="card__head"><h2>{status.route.name}</h2></div>
            <div ref={mapHolder} className="mapview__map" />
          </section>
          <section className="card card--wide">
            <div className="card__head"><h2>Street View</h2></div>
            <div ref={panoHolder} className="mapview__pano" />
            {coverage === 'no' && (
              <p className="muted">No Street View here — it picks up again further along.</p>
            )}
          </section>
        </>
      ) : (
        <p className="muted">This walk has no route on the map yet.</p>
      )}

      <p className="muted features__foot">
        A read-only view of someone else&rsquo;s walk.{' '}
        {status.expiresAt
          ? `This link stops working on ${fmt.mediumDate(status.expiresAt.slice(0, 10))}.`
          : 'This link does not expire.'}
      </p>
    </main>
  );
}
